import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { makeReference, useApolloClient } from '@apollo/client';
import { S$ } from '@infl-ts/util-fp-ts';
import { mergeSxProps } from '@infl-ts/util-merge-sx-props';
import { logQueryError } from '@infl-ts/util-query-errors';
import { Box, Typography } from '@mui/material';
import * as DE from '@nll/datum/lib/DatumEither';
import * as A from 'fp-ts/Array';
import { constant, constNull, flow, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { useCallback, useEffect } from 'react';
import { Translation } from 'react-i18next';
import PublicResponsesList, { FocusedPublicResponsesList, ResponsesPlaceholder, within10 } from './public-responses-list';
import { usePublicResponses } from './queries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var PublicResponsesContainer = function PublicResponsesContainer(_ref) {
  var totalCount = _ref.totalCount,
      children = _ref.children,
      hideTotalCount = _ref.hideTotalCount,
      sx = _ref.sx;
  return totalCount > 0 ? /*#__PURE__*/_jsx(Box, {
    sx: mergeSxProps({
      width: 1,
      background: function background(t) {
        return t.palette.secondary.light;
      },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      flexGrow: 1
    }, sx),
    children: /*#__PURE__*/_jsxs(Box, {
      sx: {
        width: 1,
        mx: 'auto',
        maxWidth: {
          md: 820
        }
      },
      children: [!hideTotalCount ? /*#__PURE__*/_jsx(Typography, {
        variant: "h3",
        sx: {
          mb: 2,
          ml: 1,
          textAlign: 'start'
        },
        children: /*#__PURE__*/_jsx(Translation, {
          children: function children(t) {
            return t("memberRespondedText", {
              count: totalCount
            });
          }
        })
      }) : null, children]
    })
  }) : null;
};

var PublicResponses = function PublicResponses(_ref2) {
  var data = _ref2.data,
      profile = _ref2.profile,
      totalCount = _ref2.totalCount,
      handleFetchMore = _ref2.handleFetchMore,
      hideTotalCount = _ref2.hideTotalCount,
      containerSx = _ref2.containerSx,
      useScrollToFocused = _ref2.useScrollToFocused;
  return pipe(data, DE.map(function (_ref3) {
    var node = _ref3.node;
    return pipe(node, O.fromNullable, O.chainNullableK(function (n) {
      return n.publicResponses;
    }), O.map(S$.partialEvolve({
      edges: flow(O.fromNullable, O.map(A.filterMap(function (edge) {
        return O.fromNullable(edge.node);
      })), O.getOrElse(constant([]))),
      pageInfo: function pageInfo(p) {
        return p !== null && p !== void 0 ? p : {
          hasNextPage: false,
          endCursor: '0'
        };
      }
    })), O.chain(O.fromPredicate(function (_ref4) {
      var edges = _ref4.edges;
      return A.isNonEmpty(edges);
    })));
  }), DE.squash(function () {
    return /*#__PURE__*/_jsx(PublicResponsesContainer, {
      sx: containerSx,
      hideTotalCount: hideTotalCount,
      totalCount: totalCount,
      children: /*#__PURE__*/_jsx(ResponsesPlaceholder, {
        count: within10(totalCount)
      })
    });
  }, flow(logQueryError, constNull), O.match(constNull, function (_ref5) {
    var edges = _ref5.edges,
        pageInfo = _ref5.pageInfo,
        totalCount = _ref5.totalCount;
    return /*#__PURE__*/_jsx(PublicResponsesContainer, {
      sx: containerSx,
      hideTotalCount: hideTotalCount,
      totalCount: totalCount,
      children: pipe({
        edges: edges,
        pageInfo: pageInfo,
        totalCount: totalCount,
        fetchMoreResponses: function fetchMoreResponses() {
          return handleFetchMore(pageInfo.endCursor);
        },
        profile: profile
      }, function (props) {
        return useScrollToFocused ? /*#__PURE__*/_jsx(FocusedPublicResponsesList, _objectSpread(_objectSpread({}, props), {}, {
          useScrollToFocused: useScrollToFocused
        })) : /*#__PURE__*/_jsx(PublicResponsesList, _objectSpread({}, props));
      })
    });
  })));
};

var useClearResponsesOnExit = function useClearResponsesOnExit(cacheId) {
  var _useApolloClient = useApolloClient(),
      cache = _useApolloClient.cache;

  useEffect(function () {
    return function () {
      cache.evict({
        id: cache.identify(makeReference(cacheId)),
        fieldName: 'publicResponses'
      });
      cache.gc();
    };
  }, [cacheId]);
};

var useProps = function useProps(_ref6) {
  var _publicResponses$tota;

  var profile = _ref6.profile,
      _ref6$stage = _ref6.stage,
      __typename = _ref6$stage.__typename,
      id = _ref6$stage.id,
      publicResponses = _ref6$stage.publicResponses,
      _ref6$hideTotalCount = _ref6.hideTotalCount,
      hideTotalCount = _ref6$hideTotalCount === void 0 ? false : _ref6$hideTotalCount,
      containerSx = _ref6.containerSx,
      useScrollToFocused = _ref6.useScrollToFocused;

  var _usePublicResponses = usePublicResponses({
    variables: {
      stageId: id,
      responseCursor: '0'
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  }),
      result = _usePublicResponses.result,
      fetchMore = _usePublicResponses.fetchMore;

  useClearResponsesOnExit("".concat(__typename, ":").concat(id));
  var handleFetchMore = useCallback(function (endCursor) {
    return fetchMore({
      variables: {
        stageId: id,
        responseCursor: endCursor
      }
    });
  }, [fetchMore, id]);
  return {
    data: result,
    handleFetchMore: handleFetchMore,
    totalCount: (_publicResponses$tota = publicResponses === null || publicResponses === void 0 ? void 0 : publicResponses.totalCount) !== null && _publicResponses$tota !== void 0 ? _publicResponses$tota : 0,
    profile: profile,
    hideTotalCount: hideTotalCount,
    containerSx: containerSx,
    useScrollToFocused: useScrollToFocused
  };
};

export default Object.assign(flow(useProps, PublicResponses), {
  displayName: 'PublicResponses'
});